import { formatHumanName } from "@medplum/core";
import { Patient } from "@repo/domain";
import { Card, CardContent, CardHeader, CardTitle } from "@repo/ui/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { edge } from "@/shared/edge/client";
import { selectedPatientFromProfile } from "@/shared/lib/patient-profile";

export const Route = createFileRoute("/_consented/profile")({ component: ProfilePage });

function ProfilePage() {
  const { profile } = Route.useRouteContext();
  const selected = selectedPatientFromProfile(profile);
  const patientId = selected?.patientId;
  const query = useQuery({
    queryKey: ["portal", "patient-profile", patientId],
    queryFn: ({ signal }) => edge.patientProfile.query(undefined, { signal, context: { patientId } }),
    enabled: !!patientId,
  });
  const patient = query.data;
  const actorHumanName = profile?.actor.reference.startsWith("Patient/")
    ? Patient.displayName(profile.actor.name)
    : profile?.actor.name[0];
  const actorName = actorHumanName ? formatHumanName(actorHumanName) : "Your account";
  const patientName = formatHumanName(Patient.displayName(selected?.name)) || "Patient";
  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Patient profile</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Your care team maintains these details. Contact them if anything needs updating.
        </p>
      </div>
      {profile && selected?.actingOnBehalf && (
        <Card className="border-blue-200 bg-blue-50 text-blue-950 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-100">
          <CardHeader>
            <CardTitle>Related person access</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-6 sm:grid-cols-2">
              <div className="min-w-0">
                <dt className="text-sm text-blue-700 dark:text-blue-300">Signed in as</dt>
                <dd className="mt-1 break-words font-medium">{actorName}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-blue-700 dark:text-blue-300">On behalf of</dt>
                <dd className="mt-1 break-words font-medium">{patientName}</dd>
              </div>
            </dl>
            <p className="mt-4 text-sm text-blue-700 dark:text-blue-300">
              You have approved portal access for {patientName}. The details below belong to this patient.
            </p>
          </CardContent>
        </Card>
      )}
      {query.isPending && <output>Loading profile…</output>}
      {query.isError && (
        <p role="alert" className="text-destructive">
          Could not load the profile. Please refresh to try again.
        </p>
      )}
      {patient && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Demographics</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid gap-6 sm:grid-cols-2">
                <Detail
                  label="Name"
                  value={Patient.legalName(patient.name) ? formatHumanName(Patient.legalName(patient.name)) : undefined}
                />
                <Detail label="Date of birth" value={patient.birthDate} />
                <Detail
                  label="Gender"
                  value={patient.gender ? patient.gender[0].toUpperCase() + patient.gender.slice(1) : undefined}
                />
              </dl>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Contact details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid gap-6 sm:grid-cols-2">
                <Detail
                  label="Email"
                  value={patient.telecom
                    .filter((contact) => contact.system === "email")
                    .map((contact) => contact.value)
                    .filter(Boolean)
                    .join(" · ")}
                />
                <Detail
                  label="Phone"
                  value={patient.telecom
                    .filter((contact) => contact.system === "phone")
                    .map((contact) => contact.value)
                    .filter(Boolean)
                    .join(" · ")}
                />
                <Detail
                  label="Address"
                  value={patient.address
                    .map(
                      (address) =>
                        address.text ||
                        [...(address.line ?? []), address.city, address.state, address.postalCode, address.country]
                          .filter(Boolean)
                          .join(", "),
                    )
                    .join(" · ")}
                />
              </dl>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
function Detail({ label, value }: { label: string; value?: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="mt-1 break-words">{value || "Not provided"}</dd>
    </div>
  );
}
